import type { World } from './world.js';
import type { EngineConfig, BossPhaseDef } from '../types.js';
import type { EventQueue } from './events.js';
import type { Boss } from './entities.js';

/**
 * Controls boss AI + phase transitions + bullet patterns.
 */
export class BossController {
  constructor(private config: EngineConfig) {}

  update(world: World, dtMs: number, events: EventQueue): void {
    const boss = world.boss;
    if (!boss || !boss.alive) return;

    boss.ageMs += dtMs;

    // Slide into play: ease toward target Y = 120, gentle horizontal sway.
    const targetY = 120;
    const dy = targetY - boss.pos.y;
    boss.pos.y += dy * Math.min(1, (dtMs / 1000) * 1.5);
    boss.pos.x =
      this.config.worldWidth * 0.5 +
      Math.sin(boss.ageMs / 800) * this.config.worldWidth * 0.22;

    // Determine phase by HP fraction.
    const def = this.config.bosses[boss.defId];
    if (!def) return;
    const hpFrac = boss.hp / boss.hpMax;

    // Pick active phase: highest index where hpThreshold >= hpFrac.
    // The plan: phase 0 at 1.0, phase 1 at 0.66, phase 2 at 0.33.
    let newPhase: 0 | 1 | 2 = 0;
    if (hpFrac <= 0.33) newPhase = 2;
    else if (hpFrac <= 0.66) newPhase = 1;

    if (newPhase !== boss.phase) {
      boss.phase = newPhase;
      events.push({ type: 'bossPhase', phase: newPhase });
      boss.fireCooldownMs = 0;
    }

    const phaseDef = def.phases[boss.phase] ?? def.phases[0];
    this.fire(world, boss, phaseDef, dtMs, events);

    if (boss.hp <= 0) {
      boss.alive = false;
      world.phase = 'cleared';
      events.push({ type: 'bossDown', pos: { x: boss.pos.x, y: boss.pos.y } });
    }
  }

  private fire(
    world: World,
    boss: Boss,
    phase: BossPhaseDef,
    dtMs: number,
    events: EventQueue
  ): void {
    if (boss.fireCooldownMs > 0) {
      boss.fireCooldownMs = Math.max(0, boss.fireCooldownMs - dtMs);
      return;
    }
    const cadenceMs = 1000 / Math.max(0.1, phase.fireRateHz);
    boss.fireCooldownMs = cadenceMs;

    switch (phase.pattern) {
      case 'fan': {
        const n = 8;
        const spreadDeg = 90;
        for (let i = 0; i < n; i++) {
          const t = i / (n - 1);
          const deg = -spreadDeg / 2 + spreadDeg * t;
          const rad = (deg * Math.PI) / 180;
          // base direction = (0, 1) = straight down
          const vx = Math.sin(rad) * phase.bulletSpeed;
          const vy = Math.cos(rad) * phase.bulletSpeed;
          world.spawnBullet({
            pos: { x: boss.pos.x, y: boss.pos.y + boss.radius },
            vel: { x: vx, y: vy },
            team: 'enemy',
            kind: 'boss',
            damage: 1,
            radius: 5,
          });
        }
        break;
      }
      case 'aimed-burst': {
        // fire 3 bullets toward the player.
        let target: { pos: { x: number; y: number } } | null = null;
        for (const p of world.players) {
          if (p.alive && p.respawnMs === 0) {
            target = p;
            break;
          }
        }
        if (!target) return;
        const dx = target.pos.x - boss.pos.x;
        const dy = target.pos.y - boss.pos.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len === 0) return;
        const nx = dx / len;
        const ny = dy / len;
        const jitters = [-6, 0, 6];
        for (const jDeg of jitters) {
          const jr = (jDeg * Math.PI) / 180;
          const cos = Math.cos(jr);
          const sin = Math.sin(jr);
          const rx = nx * cos - ny * sin;
          const ry = nx * sin + ny * cos;
          world.spawnBullet({
            pos: { x: boss.pos.x, y: boss.pos.y + boss.radius },
            vel: { x: rx * phase.bulletSpeed, y: ry * phase.bulletSpeed },
            team: 'enemy',
            kind: 'boss',
            damage: 1,
            radius: 5,
          });
        }
        break;
      }
      case 'spiral': {
        const count = 4;
        for (let i = 0; i < count; i++) {
          const angle = boss.spiralAngle + (i * Math.PI * 2) / count;
          world.spawnBullet({
            pos: { x: boss.pos.x, y: boss.pos.y + boss.radius },
            vel: {
              x: Math.cos(angle) * phase.bulletSpeed,
              y: Math.sin(angle) * phase.bulletSpeed,
            },
            team: 'enemy',
            kind: 'boss',
            damage: 1,
            radius: 5,
          });
        }
        boss.spiralAngle += 0.35;
        break;
      }
    }

    // Silence unused warning if any path returns early without using events.
    void events;
  }

  reset(): void {
    // nothing to reset in controller itself (state lives on Boss).
  }
}
